import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Badge,
  Button,
  Spinner,
  Alert,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import axios from '../../api/Axios';

const TutoringProviderDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const navigate = useNavigate();
  
  const [provider, setProvider] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    fetchProviderDetails();
  }, [id, type]);

  useEffect(() => {
    if (provider) {
      fetchProviderDocuments();
    }
  }, [provider]);

  const fetchProviderDetails = async () => {
    try {
      setLoading(true);
      // Fetch from the combined list and filter by id
      const response = await axios.get('/api/tutoring-providers/');
      const foundProvider = response.data.find(p => p.id === parseInt(id) && p._type === type);
      
      if (foundProvider) {
        setProvider(foundProvider);
        setError(null);
      } else {
        setError('Provider not found');
      }
    } catch (err) {
      console.error('Error fetching provider details:', err);
      setError('Failed to load provider details');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderDocuments = async () => {
    try {
      if (!provider || !provider.user_id) return;
      
      const response = await axios.get(`/api/provider-documents/${provider.user_id}/`);
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
      // Don't show error to user if documents fetch fails
      setDocuments([]);
    }
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const viewDocument = (doc) => {
    setSelectedDoc(doc);
    toggleModal();
  };

  const getStatusBadge = (regStatus) => {
    const statusMap = {
      0: { color: 'warning', text: 'Pending' },
      1: { color: 'info', text: 'In Progress' },
      2: { color: 'success', text: 'Complete' },
      3: { color: 'danger', text: 'Rejected' }
    };
    const status = statusMap[regStatus] || { color: 'secondary', text: 'Unknown' };
    return <Badge color={status.color}>{status.text}</Badge>;
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ? (
      <Badge color="success">
        <i className="bi bi-check-circle me-1"></i>
        Verified
      </Badge>
    ) : (
      <Badge color="warning">
        <i className="bi bi-clock me-1"></i>
        Pending
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading provider details...</p>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="container-fluid">
        <Alert color="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error || 'Provider not found'}
        </Alert>
        <Button color="secondary" onClick={() => navigate('/backoffice-dashboard/TutoringProviders')}>
          <i className="bi bi-arrow-left me-2"></i>
          Back to Providers
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button 
            color="link" 
            className="ps-0 mb-2"
            onClick={() => navigate('/backoffice-dashboard/TutoringProviders')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Providers
          </Button>
          <h2 className="mb-0">
            <i className="bi bi-mortarboard me-2"></i>
            {provider._type === 'entity' ? provider.company_name : `${provider.title || ''} ${provider.first_name} ${provider.last_name}`}
          </h2>
          <div className="mt-2">
            <Badge color={provider._type === 'individual' ? 'primary' : 'info'} className="me-2">
              <i className={`bi bi-${provider._type === 'individual' ? 'person' : 'building'} me-1`}></i>
              {provider._type === 'individual' ? 'Individual' : 'Entity'} Provider
            </Badge>
            <code className="text-primary me-2">{provider.provider_code}</code>
          </div>
        </div>
      </div>

      <Row>
        {/* Main Details */}
        <Col md="8">
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-info-circle me-2"></i>
                Provider Information
              </CardTitle>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>Provider Code</th>
                    <td><code className="text-primary">{provider.provider_code || 'N/A'}</code></td>
                  </tr>
                  <tr>
                    <th>Provider Type</th>
                    <td>
                      <Badge color={provider._type === 'individual' ? 'primary' : 'info'}>
                        {provider._type === 'individual' ? 'Individual' : 'Entity'}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Registration Status</th>
                    <td>{getStatusBadge(provider.reg_status)}</td>
                  </tr>
                  <tr>
                    <th>Created</th>
                    <td>{formatDate(provider.created_at)}</td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Personal/Company Information */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-person-badge me-2"></i>
                {provider._type === 'entity' ? 'Company & Representative Details' : 'Personal Details'}
              </CardTitle>
              <Table bordered size="sm">
                <tbody>
                  {provider._type === 'entity' && (
                    <>
                      <tr>
                        <th style={{ width: '30%' }}>Company Name</th>
                        <td>{provider.company_name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Registration Number</th>
                        <td>{provider.registration_number || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Representative</th>
                        <td>{`${provider.title || ''} ${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Representative Position</th>
                        <td>{provider.representative_position || 'N/A'}</td>
                      </tr>
                    </>
                  )}
                  {provider._type === 'individual' && (
                    <tr>
                      <th style={{ width: '30%' }}>Full Name</th>
                      <td>{`${provider.title || ''} ${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'N/A'}</td>
                    </tr>
                  )}
                  <tr>
                    <th>ID Number</th>
                    <td>{provider.id_no || 'N/A'}</td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Contact Information */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-telephone me-2"></i>
                Contact Information
              </CardTitle>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>Email</th>
                    <td>
                      <a href={`mailto:${provider.email}`}>{provider.email || 'N/A'}</a>
                    </td>
                  </tr>
                  <tr>
                    <th>Phone</th>
                    <td>{provider.contact_phone || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Physical Address</th>
                    <td>{provider.personal_address || 'N/A'}</td>
                  </tr>
                  {provider._type === 'entity' && (
                    <>
                      <tr>
                        <th>Business Address</th>
                        <td>{provider.business_address || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Business Phone</th>
                        <td>{provider.business_phone || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Business Email</th>
                        <td>
                          {provider.business_email ? (
                            <a href={`mailto:${provider.business_email}`}>{provider.business_email}</a>
                          ) : 'N/A'}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Business Description (Individual) */}
          {provider._type === 'individual' && provider.business_description && (
            <Card className="mb-4">
              <CardBody>
                <CardTitle tag="h5">
                  <i className="bi bi-card-text me-2"></i>
                  Business Description
                </CardTitle>
                <p className="mb-0">{provider.business_description}</p>
              </CardBody>
            </Card>
          )}

          {/* Banking Information */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-bank me-2"></i>
                Banking Information
              </CardTitle>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>Bank Name</th>
                    <td>{provider.bank_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Account Number</th>
                    <td>{provider.account_number || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Account Type</th>
                    <td>{provider.account_type || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Branch/Sort Code</th>
                    <td>{provider.branch_code || 'N/A'}</td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Documents Section */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-file-earmark-text me-2"></i>
                Documents
                <Button 
                  color="primary" 
                  size="sm" 
                  className="float-end"
                  onClick={toggleModal}
                >
                  <i className="bi bi-eye me-2"></i>
                  View Documents
                </Button>
              </CardTitle>
              {documents.length === 0 ? (
                <Alert color="info" className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  No documents uploaded yet
                </Alert>
              ) : (
                <Table size="sm" hover>
                  <thead>
                    <tr>
                      <th>Document Type</th>
                      <th>Status</th>
                      <th>Uploaded</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc, index) => (
                      <tr key={index}>
                        <td>{doc.doc_type}</td>
                        <td>
                          <Badge color={doc.doc_status === 'Approved' ? 'success' : 'warning'}>
                            {doc.doc_status}
                          </Badge>
                        </td>
                        <td>{formatDate(doc.uploaded_at)}</td>
                        <td>
                          <Button 
                            color="primary" 
                            size="sm"
                            onClick={() => viewDocument(doc)}
                          >
                            <i className="bi bi-eye"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col md="4">
          {/* Verification Status */}
          <Card className="mb-4">
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-shield-check me-2"></i>
                Verification Status
              </CardTitle>
              <Table borderless size="sm">
                <tbody>
                  <tr>
                    <td><strong>ID Verification</strong></td>
                    <td className="text-end">{getVerificationBadge(provider.id_verified)}</td>
                  </tr>
                  <tr>
                    <td><strong>Email Verification</strong></td>
                    <td className="text-end">{getVerificationBadge(provider.email_verified)}</td>
                  </tr>
                  {provider._type === 'entity' && (
                    <tr>
                      <td><strong>Business Certificate</strong></td>
                      <td className="text-end">{getVerificationBadge(provider.business_cert_verified)}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardBody>
              <CardTitle tag="h5">
                <i className="bi bi-lightning me-2"></i>
                Quick Actions
              </CardTitle>
              <div className="d-grid gap-2">
                <Button 
                  color="outline-primary" 
                  size="sm"
                  onClick={toggleModal}
                >
                  <i className="bi bi-file-earmark-text me-2"></i>
                  View Documents
                </Button>
                <Button 
                  color="outline-info" 
                  size="sm"
                  onClick={() => window.open(`mailto:${provider.email}`)}
                >
                  <i className="bi bi-envelope me-2"></i>
                  Send Email
                </Button>
                <Button 
                  color="outline-success" 
                  size="sm"
                  disabled={provider.reg_status === 2}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  Approve Provider
                </Button>
                <Button 
                  color="outline-danger" 
                  size="sm"
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Reject Provider
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Document Viewer Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          <i className="bi bi-file-earmark-text me-2"></i>
          Provider Documents
        </ModalHeader>
        <ModalBody>
          {documents.length === 0 ? (
            <Alert color="info">
              <i className="bi bi-info-circle me-2"></i>
              No documents have been uploaded for this provider yet.
            </Alert>
          ) : selectedDoc ? (
            <div>
              <h5>{selectedDoc.doc_type}</h5>
              <p><strong>Status:</strong> {selectedDoc.doc_status}</p>
              <p><strong>Uploaded:</strong> {formatDate(selectedDoc.uploaded_at)}</p>
              {selectedDoc.comments && (
                <p><strong>Comments:</strong> {selectedDoc.comments}</p>
              )}
              {selectedDoc.expiry_date && (
                <p><strong>Expiry Date:</strong> {selectedDoc.expiry_date}</p>
              )}
              <div className="text-center p-4 border rounded">
                {selectedDoc.document_url ? (
                  <>
                    <iframe 
                      src={selectedDoc.document_url} 
                      style={{ width: '100%', height: '500px', border: 'none' }}
                      title="Document Preview"
                    />
                    <Button 
                      color="primary" 
                      size="sm"
                      className="mt-3"
                      onClick={() => window.open(selectedDoc.document_url, '_blank')}
                    >
                      <i className="bi bi-download me-2"></i>
                      Download Document
                    </Button>
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-pdf" style={{ fontSize: '3rem' }}></i>
                    <p className="mt-2">Document not available</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <Table hover size="sm">
              <thead>
                <tr>
                  <th>Document Type</th>
                  <th>Status</th>
                  <th>Uploaded</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc, index) => (
                  <tr key={index}>
                    <td>{doc.doc_type}</td>
                    <td>
                      <Badge color={doc.doc_status === 'Approved' ? 'success' : 'warning'}>
                        {doc.doc_status}
                      </Badge>
                    </td>
                    <td>{formatDate(doc.uploaded_at)}</td>
                    <td>
                      <Button 
                        color="primary" 
                        size="sm"
                        onClick={() => viewDocument(doc)}
                      >
                        <i className="bi bi-eye me-2"></i>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TutoringProviderDetails;
